/**
 * The /me onboarding answer VOCABULARIES — every option key the flow can persist,
 * bound to the sentence the client read when they chose it.
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 * An onboarding answer persists as the bare key and nothing else: `updateAnswers`
 * → `answersToWire` → `PATCH /me/partners/{id}/answers` → merged into
 * `ConversationParticipantOnboarding.form_data` as JSONB. No label travels with
 * it, and no schema version. So the label sitting beside the key in THIS file is
 * the only surviving definition of what a stored key means.
 *
 * That made a silent remap possible, and one happened: `intimacy` was
 * "Bringing back intimacy and closeness" and became "Improving sex life and
 * desire" — a different question, answered by rows that were already stored.
 * Renaming a label on an existing key rewrites history for everyone who already
 * answered it, and nothing in the type system or the suite could see it.
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 * A key's label is FROZEN once the key ships. To ask a new question, ADD A NEW
 * KEY. `../labelLock.ts` enforces exactly that, at typecheck time.
 *
 * Copy-editing an existing label (a typo, punctuation) is still possible — it
 * just cannot be silent: you update the lock in the same diff, which is the
 * moment to ask whether the meaning moved. That question is the whole point.
 *
 * ── One definition site ─────────────────────────────────────────────────────
 * The key unions in `../types.ts` are DERIVED from these lists rather than
 * hand-written beside them, so a key cannot exist in the union with no option,
 * or in an option with no union member. Declaration order here is render order.
 */

// The list/label type machinery lives with the lock that uses it. Type-only, so
// this does not make a runtime cycle with `labelLock.ts` importing these lists.
import type { KeyOf, OptionList } from "../labelLock";

// Ordered as a life-stage progression: dating → engaged → married →
// separated, with the "other" catch-all last.
export const RELATIONSHIP_STATUS_OPTIONS = [
  { value: "dating", label: "Dating" },
  { value: "engaged", label: "Engaged" },
  { value: "married", label: "Married" },
  { value: "separated", label: "Separated" },
  { value: "other", label: "Other" },
] as const;

export const RELATIONSHIP_LENGTH_OPTIONS = [
  { value: "lt_1y", label: "Less than 1 year" },
  { value: "1_3y", label: "1–3 years" },
  { value: "3_5y", label: "3–5 years" },
  { value: "5_10y", label: "5–10 years" },
  { value: "10plus", label: "10+ years" },
] as const;

export const CHILDREN_OPTIONS = [
  { value: "none", label: "No children" },
  { value: "trying", label: "Trying for kids" },
  { value: "expecting", label: "Baby on the way" },
  { value: "young", label: "Young children" },
  { value: "grown", label: "Grown children" },
] as const;

// Re-exported, NOT redefined. The therapist portal shows these same answers
// back to a clinician, and a second copy of the labels is a second definition
// of what every stored key means — the exact failure the label lock below
// exists to catch. The list now lives in `@codel/dova-ds/answerVocabulary`,
// the only package both apps depend on. Every import here, and the lock
// itself, still resolves to that one definition.
import { FOCUS_AREA_OPTIONS } from "@codel/dova-ds";
export { FOCUS_AREA_OPTIONS };

export type RelationshipStatus = KeyOf<typeof RELATIONSHIP_STATUS_OPTIONS>;
export type RelationshipLength = KeyOf<typeof RELATIONSHIP_LENGTH_OPTIONS>;
export type ChildrenStatus = KeyOf<typeof CHILDREN_OPTIONS>;
export type FocusArea = KeyOf<typeof FOCUS_AREA_OPTIONS>;

/**
 * Every vocabulary in one place, so the guard test is ONE parametrised sweep
 * rather than one hand-written case per step. A new vocabulary that is not
 * registered here is not guarded, so registering it is part of adding one —
 * the label-lock guard test asserts this roster covers every list exported above.
 */
export const ANSWER_VOCABULARIES = {
  relationship_status: RELATIONSHIP_STATUS_OPTIONS,
  relationship_length: RELATIONSHIP_LENGTH_OPTIONS,
  children: CHILDREN_OPTIONS,
  focus_areas: FOCUS_AREA_OPTIONS,
} as const satisfies Record<string, OptionList>;

/** The wire field each vocabulary lands in (`AnswersPatch` / `form_data` key). */
export type AnswerField = keyof typeof ANSWER_VOCABULARIES;

// ── The key set the SERVER accepts must be the key set we offer ─────────────
//
// `PATCH /me/partners/{id}/answers` now declares these vocabularies as closed
// (`api/src/services/couple_resolution.py`), and orval regenerates them into
// `AnswersPatch`. Comparing the two sets HERE is what turns "they happen to
// match" into "they cannot diverge": previously the server typed every one of
// these as a bare `str`, so a key added on one side and forgotten on the other
// was a runtime 422 for a client mid-flow, or worse, a silently accepted token
// with no definition on either side.
//
// Both directions fail, and they fail differently:
//   • a key we offer that the server refuses → clients hit a 422 on submit;
//   • a key the server accepts that we do not offer → a vocabulary entry with no
//     way to choose it, and no label, i.e. a stored value nothing can explain.

import type { AnswersPatch } from "@/__generated__/api/wrenAPI.schemas";
import type { LockHolds } from "../labelLock";

type ServerKeys<F extends keyof AnswersPatch> = NonNullable<AnswersPatch[F]>;

type KeySetMismatch<Field extends string, Ours, Theirs> =
  | (Exclude<Ours, Theirs> extends never
      ? never
      : `KEY SET MISMATCH on "${Field}" — the form offers ${Exclude<Ours, Theirs> &
          string}, which the server refuses. Add it to the Literal in api/src/services/couple_resolution.py and regenerate the client.`)
  | (Exclude<Theirs, Ours> extends never
      ? never
      : `KEY SET MISMATCH on "${Field}" — the server accepts ${Exclude<Theirs, Ours> &
          string}, which no option offers. A key with no option has no label, so nothing records what it means.`);

export type RelationshipStatusAgrees = LockHolds<
  KeySetMismatch<"relationship_status", RelationshipStatus, ServerKeys<"relationship_status">>
>;
export type RelationshipLengthAgrees = LockHolds<
  KeySetMismatch<"relationship_length", RelationshipLength, ServerKeys<"relationship_length">>
>;
export type ChildrenAgrees = LockHolds<
  KeySetMismatch<"children", ChildrenStatus, ServerKeys<"children">>
>;
export type FocusAreaAgrees = LockHolds<
  KeySetMismatch<"focus_areas", FocusArea, ServerKeys<"focus_areas">[number]>
>;

// ── The FREE-TEXT keys, and the question each one answers ───────────────────
//
// The same defect in a different shape. A free-text answer persists under a bare
// key too (`six_months_from_now`, `focus_areas_other`), and what a client typed
// is only interpretable against the question they were asked. The question lives
// in the step's JSX, so rewording it silently re-labels every sentence already
// stored — the answers do not change, but what they are answers TO does.
//
// It has already drifted once, in the opposite direction from `intimacy`: the key
// still says `six_months_from_now`, and the question no longer mentions six
// months at all — it asks for "your dream". The stored key and the stored text
// now describe different questions, and only the key survives into the database.
//
// Locked in `me/labelLock.ts` on the same terms as an option label. Renaming the
// KEY is the way to ask a different question; rewording the question in place is
// what has to be visible.
export const FREE_TEXT_QUESTIONS = [
  { value: "six_months_from_now", label: "What's your dream for your relationship?" },
  { value: "focus_areas_other", label: "What else would you like to focus on?" },
  { value: "relationship_status_other", label: "Describe your relationship" },
] as const;

export type FreeTextAnswerKey = KeyOf<typeof FREE_TEXT_QUESTIONS>;

/** Question text by key, for the steps that render it. */
export const FREE_TEXT_QUESTION: Record<FreeTextAnswerKey, string> = Object.fromEntries(
  FREE_TEXT_QUESTIONS.map((q) => [q.value, q.label])
) as Record<FreeTextAnswerKey, string>;
